<template>
  <tr
    :class="
      product && renderFullRow
        ? {
            'full-row': true,
            'active-row': tableUtilityStore.tableSelection.rowIsSelected(rowIndex),
            'selected-row': tableUtilityStore.tableSelection.cellIsSelected({ columnIndex: -1, rowIndex }),
          }
        : undefined
    "
  >
    <template v-if="product">
      <!-- Index -->
      <th
        v-if="renderFullRow"
        class="index"
        :class="[
          {
            modified: bulkStore.isModified(product) || bulkStore.newProductsSkus?.has(product.sku),
            'focused-index': focusedIndexRow === rowIndex,
            'hovered-index': hoveredIndexRow === rowIndex,
          },
          productAvailabilityCss,
        ]"
        @mouseenter="
          emit('update:hoveredIndexRow', rowIndex), tableUtilityStore.tableSelection.handleSelectionDrag({ columnIndex: -1, rowIndex }, selectionLimit)
        "
        @mouseleave="emit('update:hoveredIndexRow')"
        @mousedown.left="
          emit('update:focusedIndexRow', focusedIndexRow === rowIndex ? undefined : rowIndex),
            emit('update:hoveredIndexRow'),
            tableUtilityStore.tableSelection.startSelectionDrag(
              { columnIndex: -1, rowIndex },
              'column',
              () => {
                tableUtilityStore.tableSelection.selectedCells.length > 1 ? emit('update:focusedIndexRow') : 0
              },
              selectionLimit,
              $event,
            )
        "
        @mousedown.exact.shift.left="tableUtilityStore.tableSelection.extendSelectionTo({ columnIndex: -1, rowIndex }, selectionLimit)"
        @contextmenu="
          emit('update:focusedIndexRow'),
            tableUtilityStore.tableSelection.cellIsSelected({ columnIndex: -1, rowIndex })
              ? 0
              : tableUtilityStore.tableSelection.setCellSelection({ columnIndex: -1, rowIndex }),
            tableUtilityStore.openIndexContextMenu($event, isProtected)
        "
      >
        {{ rowIndex + 1 }}
      </th>
      <!-- Render placeholder index if row not in view to occupy row space -->
      <th v-else class="index">
        {{ rowIndex + 1 }}
      </th>

      <template v-if="renderFullRow">
        <!-- Sticky cells -->
        <TableCell
          v-for="(property, columnIndex) of bulkStore.stickyProperties"
          :key="property.defaultHeader"
          :cell="{ columnIndex: columnIndex, rowIndex }"
          :property="property"
          :product="product"
          :selection-limit="selectionLimit"
          class="horizontally-sticky"
          :class="{
            sku: isSkuProperty(property),
            'sticky-shadow': shouldShowShadow(columnIndex),
          }"
          :style="getStickyColumnStyle(columnIndex)"
          @selection="emit('selection', $event)"
          @focus-cell="emit('focus-cell', $event)"
        />

        <!-- Placeholder cell to fill up space on the left -->
        <td v-if="leftColspan > 0" :colspan="leftColspan" />

        <!-- Visible cells -->
        <TableCell
          v-for="(property, i) of visibleProperties"
          :key="property.defaultHeader"
          :cell="{ columnIndex: i + firstVisiblePropertyIndex, rowIndex }"
          :property="property"
          :product="product"
          :selection-limit="selectionLimit"
          :is-protected="isProtected"
          :class="{ 'first-non-sticky': i === 0 }"
          @selection="emit('selection', $event)"
          @focus-cell="emit('focus-cell', $event)"
        />
      </template>
    </template>

    <!-------------->
    <!-- Skeleton -->
    <!-------------->
    <template v-else>
      <!-- Index -->
      <th class="index">
        {{ rowIndex + 1 }}
      </th>

      <template v-if="renderFullRow">
        <!-- Sticky cells -->
        <td
          v-for="(property, columnIndex) of bulkStore.stickyProperties"
          :key="columnIndex"
          class="data horizontally-sticky"
          :class="{
            sku: isSkuProperty(property),
            'sticky-shadow': shouldShowShadow(columnIndex),
          }"
          :style="getStickyColumnStyle(columnIndex)"
        />

        <!-- Placeholder cell to fill up space on the left -->
        <td v-if="leftColspan > 0" :colspan="leftColspan" />

        <!-- Visible skeleton cells -->
        <td v-for="(_, i) of visibleProperties" :key="i" class="data" />
      </template>
    </template>
  </tr>
</template>

<script setup lang="ts">
import { SimplifiedProduct } from '@/models/Product'
import { EditableAdvancedProperty } from '@/models/Property'
import { useProductsBulkStore } from '@/store/productsBulkStore'
import { useProductsTableUtilityStore } from '@/store/productsTableUtilityStore'
import { isSkuProperty } from '@/utils/propertyUtils'
import { computed, toRef } from 'vue'
import TableCell from './TableCell.vue'
import { useProductFunctions } from '@/composables/useProduct'

const props = defineProps<{
  rowIndex: number
  renderFullRow: boolean // whether to render data row or a simple placeholder
  visibleProperties: EditableAdvancedProperty[]
  firstVisiblePropertyIndex: number
  product?: SimplifiedProduct
  focusedIndexRow?: number
  hoveredIndexRow?: number
  isProtected?: boolean
  getStickyColumnStyle: (columnIndex: number) => string | undefined
  shouldRenderColumn: (columnIndex: number) => boolean
}>()

const emit = defineEmits(['focus-cell', 'update:hoveredIndexRow', 'update:focusedIndexRow', 'selection'])

const bulkStore = useProductsBulkStore()
const tableUtilityStore = useProductsTableUtilityStore()
const { productAvailabilityCss } = useProductFunctions(toRef(props, 'product'))

const selectionLimit = computed(() => (props.isProtected ? 'section2' : 'section1'))
const leftColspan = computed(() => props.firstVisiblePropertyIndex - bulkStore.stickyProperties.length)

const shouldShowShadow = (columnIndex: number) => tableUtilityStore.isSideScrolled && columnIndex === bulkStore.stickyProperties.length - 1
</script>
